import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Icon
} from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './BlacklistedPatternPage.css';

const BlacklistedPatternPage = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [pattern, setPattern] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadPatterns = async (page = 0, size = 20) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/blacklisted_pattern?page=${page}&size=${size}`
      );
      const data = res.data;
      const content = data.content || [];
      setTableData(
        content.map((item) => ({
          key: item.id,
          id: item.id,
          pattern: item.pattern,
          createdAt: item.createdAt
        }))
      );
      setPagination((prev) => ({
        ...prev,
        current: (data.number ?? page) + 1,
        pageSize: data.size ?? size,
        total: data.totalElements ?? content.length
      }));
    } catch (err) {
      console.error('Failed to load patterns:', err);
      message.error('Failed to load patterns.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatterns();
  }, []);

  const handleTableChange = (newPagination) => {
    const page = newPagination.current - 1;
    const size = newPagination.pageSize;
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: size
    }));
    loadPatterns(page, size);
  };

  const handleAdd = async () => {
    if (!pattern.trim()) {
      message.error('Pattern is required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/blacklisted_pattern`, {
        pattern: pattern.trim()
      });
      message.success('Pattern created successfully.');
      setModalVisible(false);
      setPattern('');
      loadPatterns(pagination.current - 1, pagination.pageSize);
    } catch (err) {
      console.error('Failed to create pattern:', err);
      message.error('Failed to create pattern.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    try {
      await axios.delete(`${BASE_URL}/blacklisted_pattern/${record.id}`);
      message.success('Pattern deleted successfully.');
      loadPatterns(pagination.current - 1, pagination.pageSize);
    } catch (err) {
      console.error('Failed to delete pattern:', err);
      message.error('Failed to delete pattern.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(
      2,
      '0'
    )}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const columns = [
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val) => formatDate(val)
    },
    {
      title: 'Pattern',
      dataIndex: 'pattern',
      key: 'pattern'
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title='Delete this pattern?'
          onConfirm={() => handleDelete(record)}
          okText='Yes'
          cancelText='No'
        >
          <Button
            size='small'
            className='delete-btn'
            loading={deletingId === record.id}
          >
            {deletingId !== record.id && (
              <Icon type='delete' style={{ color: '#ff4d4f' }} />
            )}
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div className='blacklisted-pattern-page'>
      <div className='blacklisted-pattern-page__header'>
        <h2 className='blacklisted-pattern-page__title'>
          Blacklisted Pattern Management
        </h2>
        <Button type='primary' onClick={() => setModalVisible(true)}>
          Add New
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowKey='id'
        size='middle'
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['20', '50', '100']
        }}
        onChange={handleTableChange}
      />

      <Modal
        title='Add New Pattern'
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setPattern('');
        }}
        footer={[
          <Button
            key='cancel'
            onClick={() => {
              setModalVisible(false);
              setPattern('');
            }}
          >
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={submitting}
            onClick={handleAdd}
          >
            Save
          </Button>
        ]}
        width={480}
      >
        <Form layout='vertical'>
          <Form.Item
            label={
              <span>
                PATTERN <span style={{ color: 'red' }}>*</span>
              </span>
            }
          >
            <Input
              placeholder='.example'
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              onPressEnter={handleAdd}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlacklistedPatternPage;
